(() => {
  if (window.CBHSiteMapDirectory) return;

  const rootSelector = "[data-cbh-sitemap]";
  const mobileArticleQuery = "(max-width: 749px)";

  const normalise = (value) => {
    const text = String(value || "").toLowerCase();
    return typeof text.normalize === "function"
      ? text.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      : text;
  };

  const initialise = (root) => {
    if (!root || root.dataset.sitemapReady === "true") return;

    const input = root.querySelector("[data-sitemap-search]");
    const status = root.querySelector("[data-sitemap-status]");
    const emptyState = root.querySelector("[data-sitemap-empty]");
    const clearButtons = Array.from(root.querySelectorAll("[data-sitemap-clear]"));
    const items = Array.from(root.querySelectorAll("[data-sitemap-item]"));
    const sections = Array.from(root.querySelectorAll("[data-sitemap-section]"));
    const subgroups = Array.from(root.querySelectorAll("[data-sitemap-subgroup]"));
    const letterGroups = Array.from(root.querySelectorAll("[data-sitemap-letter-group]"));
    const letterLinks = Array.from(root.querySelectorAll("[data-sitemap-letter-link]"));
    const articleGroups = Array.from(root.querySelectorAll("[data-sitemap-article-group]"));
    const articleMedia = window.matchMedia(mobileArticleQuery);
    const articleMobileStates = new Map();

    root.dataset.sitemapEnhanced = "true";
    root.dataset.sitemapReady = "true";

    if (!input || !status || !items.length) return;

    const inputClear = root.querySelector(
      ".cbh-sitemap__search-control [data-sitemap-clear]",
    );
    const total = items.length;
    let searchActive = false;

    const groupHasVisibleItems = (group) =>
      Array.from(group.querySelectorAll("[data-sitemap-item]")).some(
        (item) => !item.hidden,
      );

    const articleGroupLabel = (group) =>
      group.querySelector(".cbh-sitemap__article-group-title")?.textContent?.trim() ||
      "this article library";

    const setArticleGroupOpen = (group, open, persist = false) => {
      const content = group.querySelector("[data-sitemap-article-content]");
      const toggle = group.querySelector("[data-sitemap-article-toggle]");
      const toggleLabel = group.querySelector("[data-sitemap-article-toggle-label]");

      if (!content || !toggle) return;

      if (persist) articleMobileStates.set(group, open);

      const visuallyOpen = articleMedia.matches ? open : true;
      content.hidden = !visuallyOpen;
      toggle.setAttribute("aria-expanded", String(visuallyOpen));
      toggle.disabled = articleMedia.matches && searchActive;
      group.dataset.sitemapArticleCollapsed = String(!visuallyOpen);

      if (toggleLabel) {
        toggleLabel.textContent = `${visuallyOpen ? "Hide" : "Show"} guides in ${articleGroupLabel(group)}`;
      }
    };

    const syncArticleGroups = (query) => {
      searchActive = Boolean(query);
      root.dataset.sitemapSearchActive = String(searchActive);

      articleGroups.forEach((group) => {
        if (!articleMobileStates.has(group)) articleMobileStates.set(group, false);

        let shouldOpen = articleMobileStates.get(group) === true;
        if (!articleMedia.matches) shouldOpen = true;
        if (articleMedia.matches && searchActive && groupHasVisibleItems(group)) {
          shouldOpen = true;
        }

        setArticleGroupOpen(group, shouldOpen);
      });
    };

    const updateLetterNavigation = (query) => {
      letterLinks.forEach((link) => {
        const href = link.getAttribute("href");
        if (!href || !href.startsWith("#")) return;

        const target = root.querySelector(href);
        link.hidden = Boolean(query) && (!target || target.hidden);
      });
    };

    const applyFilter = () => {
      const rawQuery = input.value.trim();
      const query = normalise(rawQuery);
      let visibleCount = 0;

      items.forEach((item) => {
        const searchValue = normalise(item.dataset.sitemapSearchValue);
        const matches = query === "" || searchValue.includes(query);
        item.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      letterGroups.forEach((group) => {
        group.hidden = !groupHasVisibleItems(group);
      });

      subgroups.forEach((group) => {
        group.hidden = !groupHasVisibleItems(group);
      });

      sections.forEach((section) => {
        section.hidden = !groupHasVisibleItems(section);
      });

      syncArticleGroups(query);
      updateLetterNavigation(query);

      if (rawQuery === "") {
        status.textContent = `${total} directory entries available`;
      } else {
        status.textContent = `${visibleCount} ${visibleCount === 1 ? "match" : "matches"} for “${rawQuery}”`;
      }

      if (inputClear) inputClear.hidden = rawQuery === "";
      if (emptyState) emptyState.hidden = visibleCount !== 0;
    };

    const clearFilter = () => {
      input.value = "";
      applyFilter();
      input.focus();
    };

    articleGroups.forEach((group) => {
      articleMobileStates.set(group, false);
      const toggle = group.querySelector("[data-sitemap-article-toggle]");
      if (!toggle) return;

      toggle.addEventListener("click", () => {
        if (!articleMedia.matches || searchActive) return;
        const currentState = articleMobileStates.get(group) === true;
        setArticleGroupOpen(group, !currentState, true);
      });
    });

    const handleArticleBreakpointChange = () => {
      syncArticleGroups(normalise(input.value.trim()));
    };

    if (typeof articleMedia.addEventListener === "function") {
      articleMedia.addEventListener("change", handleArticleBreakpointChange);
    } else if (typeof articleMedia.addListener === "function") {
      articleMedia.addListener(handleArticleBreakpointChange);
    }

    input.addEventListener("input", applyFilter);
    input.addEventListener("search", applyFilter);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && input.value) {
        event.preventDefault();
        clearFilter();
      }
    });

    clearButtons.forEach((button) => {
      button.addEventListener("click", clearFilter);
    });

    applyFilter();
  };

  const initialiseWithin = (container) => {
    if (!container) return;
    if (container.matches?.(rootSelector)) initialise(container);
    container.querySelectorAll?.(rootSelector).forEach(initialise);
  };

  window.CBHSiteMapDirectory = { initialiseWithin };
  initialiseWithin(document);

  document.addEventListener("shopify:section:load", (event) => {
    initialiseWithin(event.target);
  });
})();
